import React from 'react';
import { Field, ErrorMessage } from 'formik';

/**
 * CommonFinalFields - Reusable component for Final Interpretation and Remarks fields
 * that appear at the bottom of test results after test-specific fields
 * 
 * @param {Object} props - Component props
 * @param {string} props.testType - The current test type to determine Final Interpretation options
 * @param {Object} props.values - Formik values object
 * @param {Function} props.setFieldValue - Formik setFieldValue function
 * @param {Array} props.customInterpretationOptions - Custom options for Final Interpretation (overrides default)
 * @param {boolean} props.hideInterpretation - Whether to hide Final Interpretation field (default: false)
 * @param {string} props.interpretationFieldName - Custom field name for Final Interpretation (default: 'FinalInterpretation')
 * @param {string} props.remarksFieldName - Custom field name for Remarks (default: 'Remarks')
 * @param {string} props.className - Additional CSS classes for the container
 */
const CommonFinalFields = ({
  testType,
  values,
  setFieldValue,
  customInterpretationOptions = null,
  hideInterpretation = false,
  interpretationFieldName = 'FinalInterpretation',
  remarksFieldName = 'Remarks',
  className = ""
}) => {
  
  // Helper function to create SC (Scanty) options
  const createScantyOptions = (count) => {
    const options = [];
    for (let i = 1; i <= count; i++) {
      const key = `Positive (SC-${i})`;
      options.push({ key: key, value: key, label: key });
    }
    return options;
  };

  // Base options for ZN and Fluorescent
  const MICROSCOPY_BASE_OPTIONS = [
    { key: "Negative", value: "Negative", label: "Negative" },
    { key: "Positive", value: "Positive", label: "Positive" },
    { key: "Positive (1+)", value: "Positive (1+)", label: "Positive (1+)" },
    { key: "Positive (2+)", value: "Positive (2+)", label: "Positive (2+)" },
    { key: "Positive (3+)", value: "Positive (3+)", label: "Positive (3+)" },
  ];

  // Final Interpretation options by test type
  const getFinalInterpretationOptions = (currentTestType) => {
    // If custom options are provided, use them
    if (customInterpretationOptions) {
      return customInterpretationOptions;
    }

    switch (currentTestType) {
      case 'Microscopy ZN':
        return [
          ...MICROSCOPY_BASE_OPTIONS,
          ...createScantyOptions(9)
        ];
      
      case 'Microscopy Fluorescent':
        return [
          ...MICROSCOPY_BASE_OPTIONS,
          ...createScantyOptions(20)
        ];
      
      case 'CBNAAT':
        return [
          { key: "M.Tb. not detected", value: "M.Tb. not detected", label: "M.Tb. not detected" },
          { key: "M.Tb. detected, Rif resistance not detected", value: "M.Tb. detected, Rif resistance not detected", label: "M.Tb. detected, Rif resistance not detected" },
          { key: "M.Tb. detected, Rif resistance indeterminate", value: "M.Tb. detected, Rif resistance indeterminate", label: "M.Tb. detected, Rif resistance indeterminate" },
          { key: "M.Tb. detected, Rif resistance detected", value: "M.Tb. detected, Rif resistance detected", label: "M.Tb. detected, Rif resistance detected" },
          { key: "No result", value: "No result", label: "No result" },
          { key: "Error", value: "Error", label: "Error" },
          { key: "Invalid", value: "Invalid", label: "Invalid" },
        ];
      
      case 'Truenat (MTB)':
        return [
          { key: "M.Tb. not detected", value: "M.Tb. not detected", label: "M.Tb. not detected" },
          { key: "M.Tb. detected", value: "M.Tb. detected", label: "M.Tb. detected" },
          { key: "Error", value: "Error", label: "Error" },
          { key: "Invalid", value: "Invalid", label: "Invalid" },
          { key: "DNA extraction error", value: "DNA extraction error", label: "DNA extraction error" },
        ];
      
      case 'Truenat (MTB-RIF)':
        return [
          { key: "M.Tb. detected, Rif resistance not detected", value: "M.Tb. detected, Rif resistance not detected", label: "M.Tb. detected, Rif resistance not detected" },
          { key: "M.Tb. detected, Rif resistance indeterminate", value: "M.Tb. detected, Rif resistance indeterminate", label: "M.Tb. detected, Rif resistance indeterminate" },
          { key: "M.Tb. detected, Rif resistance detected", value: "M.Tb. detected, Rif resistance detected", label: "M.Tb. detected, Rif resistance detected" },
          { key: "M.Tb. detected, Rif resistance error", value: "M.Tb. detected, Rif resistance error", label: "M.Tb. detected, Rif resistance error" },
          { key: "Error", value: "Error", label: "Error" },
        ];
      
      case 'Culture':
        return [
          { key: "Negative", value: "Negative", label: "Negative" },
          { key: "Positive", value: "Positive", label: "Positive" },
          { key: "Contaminated", value: "Contaminated", label: "Contaminated" },
          { key: "NTM", value: "NTM", label: "NTM" },
          { key: "Other(Specify in Remarks)", value: "Other(Specify in Remarks)", label: "Other(Specify in Remarks)" },
        ];
      
      case 'DST':
        return [
          { key: "Negative", value: "Negative", label: "Negative" },
          { key: "Positive", value: "Positive", label: "Positive" },
          { key: "Positive (1+)", value: "Positive (1+)", label: "Positive (1+)" },
          { key: "Positive (2+)", value: "Positive (2+)", label: "Positive (2+)" },
          { key: "Positive (3+)", value: "Positive (3+)", label: "Positive (3+)" },
          { key: "Positive (SC-1)", value: "Positive (SC-1)", label: "Positive (SC-1)" },
          { key: "Positive (SC-2)", value: "Positive (SC-2)", label: "Positive (SC-2)" },
          { key: "Positive (SC-3)", value: "Positive (SC-3)", label: "Positive (SC-3)" },
          { key: "Positive (SC-4)", value: "Positive (SC-4)", label: "Positive (SC-4)" },
          { key: "Positive (SC-5)", value: "Positive (SC-5)", label: "Positive (SC-5)" },
          { key: "Positive (SC-6)", value: "Positive (SC-6)", label: "Positive (SC-6)" },
          { key: "Positive (SC-7)", value: "Positive (SC-7)", label: "Positive (SC-7)" },
          { key: "Positive (SC-8)", value: "Positive (SC-8)", label: "Positive (SC-8)" },
          { key: "Positive (SC-9)", value: "Positive (SC-9)", label: "Positive (SC-9)" },
        ];
      
      case 'FLLPA':
      // For FLLPA, Final Interpretation is dynamic based on Result value
      // If no custom options provided, return empty array
      return [];
      
      case 'SLLPA':
        return [
          { key: "Clinically Diagnosed TB", value: "Clinically Diagnosed TB", label: "Clinically Diagnosed TB" },
          { key: "Not Suggestive of TB", value: "Not Suggestive of TB", label: "Not Suggestive of TB" },
        ];
      
      case 'Chest X Ray':
        return [
          { key: "Not Suggestive of TB", value: "Not Suggestive of TB", label: "Not Suggestive of TB" },
          { key: "Suggestive of TB", value: "Suggestive of TB", label: "Suggestive of TB" },
        ];
      
      case 'Cytopathology':
      case 'Histopathology':
      case 'Gene Sequencing':
      case 'Pathodetect - MTB, Rif & Inh':
      case 'Other':
        // For these test types, Final Interpretation options need to be defined
        // Currently returning empty array - should be populated based on requirements
        return [
          { key: "Clinically Diagnosed TB", value: "Clinically Diagnosed TB", label: "Clinically Diagnosed TB" },
          { key: "Not Suggestive of TB", value: "Not Suggestive of TB", label: "Not Suggestive of TB" },
        ];
      
      case 'TST':
        return [
          { key: "Negative", value: "Negative", label: "Negative" },
          { key: "Positive", value: "Positive", label: "Positive" },
          { key: "Indeterminate", value: "Indeterminate", label: "Indeterminate" },
        ];

      case 'IGRA':
        return [
          { key: "Negative", value: "Negative", label: "Negative" },
          { key: "Positive", value: "Positive", label: "Positive" },
          { key: "Indeterminate", value: "Indeterminate", label: "Indeterminate" },
        ];

      case 'Cy-Tb':
        return [
          { key: "Negative", value: "Negative", label: "Negative" },
          { key: "Positive", value: "Positive", label: "Positive" },
        ];

      default:
        return [];
    }
  };

  const interpretationOptions = getFinalInterpretationOptions(testType);

  return (
    <div className={`row mt-3 ${className}`}>
      {/* Final Interpretation - Required (unless hidden) */}
      {!hideInterpretation && (
        <div className="col-md-6 mt-3">
          <div className="form-group">
            <label htmlFor={interpretationFieldName} className="form-label">
              <span style={{ color: "red", fontSize: "20px" }}>*</span>
              Final Interpretation
            </label>
            <Field 
              as="select" 
              id={interpretationFieldName} 
              name={interpretationFieldName} 
              className="form-control input-width select"
            >
              <option disabled hidden value="">Select Final Interpretation</option>
              {interpretationOptions.map(option => (
                <option key={option.key} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Field>
            <ErrorMessage name={interpretationFieldName} component="div" className="text-danger" />
          </div>
        </div>
      )}

      {/* Remarks - Optional */}
      <div className="col-md-6 mt-3">
        <div className="form-group">
          <label htmlFor={remarksFieldName} className="form-label">
            Remarks
          </label>
          <Field 
            as="textarea" 
            id={remarksFieldName} 
            name={remarksFieldName} 
            maxLength="255" 
            rows="3" 
            className="form-control input-width"
            placeholder="Enter remarks (optional)"
          />
          <ErrorMessage name={remarksFieldName} component="div" className="text-danger" />
        </div>
      </div>
    </div>
  );
};

export default CommonFinalFields;